import axios from 'axios'
import { BASE_URL } from '../constants/config'
import { getJwtToken, clearJwtTokenCache } from '../utils/jiraContext'
import { emitLicenseRequired } from '../utils/licenseManager'

/**
 * ATTACHMENTS API
 *
 * API client for attachment operations on test cases, test steps, and executions.
 *
 * Endpoints:
 * - GET    /attachments/testcases/{testCaseId}              - List test case attachments
 * - POST   /attachments/testcases/{testCaseId}              - Upload to test case
 * - GET    /attachments/teststeps/{testStepId}              - List test step attachments
 * - POST   /attachments/teststeps/{testStepId}              - Upload to test step
 * - GET    /attachments/testcase-executions/{executionId}   - List execution attachments
 * - POST   /attachments/testcase-executions/{executionId}   - Upload to execution
 * - GET    /attachments/teststep-executions/{executionId}   - List step execution attachments
 * - POST   /attachments/teststep-executions/{executionId}   - Upload to step execution
 * - GET    /attachments/{attachmentId}/download             - Download attachment
 * - DELETE /attachments/{attachmentId}                       - Delete attachment
 */

// Create axios instance for attachments API
const attachmentsClient = axios.create({
  baseURL: BASE_URL,
  timeout: 60000 // 60 second timeout for file uploads
})

/**
 * REQUEST INTERCEPTOR
 * Attaches JWT token to all requests.
 */
attachmentsClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await getJwtToken()
      if (token) {
        config.headers.Authorization = `Bearer ${token}`
      }
    } catch (error) {
      console.error('Failed to get JWT token for attachments API:', error)
    }
    return config
  },
  (error) => Promise.reject(error)
)

/**
 * RESPONSE INTERCEPTOR
 * Handles authentication and specific error codes.
 */
attachmentsClient.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('Attachments API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.response?.data?.message || error.message
    })

    // Handle license validation failures - 402 means license is invalid/expired
    if (error.response?.status === 402) {
      emitLicenseRequired(error.response?.data?.message || 'License required')
    }

    if (error.response?.status === 401) {
      clearJwtTokenCache()
    }

    // Extract meaningful error message for UI display
    const errorMessage = error.response?.data?.message || error.message || 'An error occurred'
    error.displayMessage = errorMessage

    return Promise.reject(error)
  }
)

// ============================================================================
// TEST CASE ATTACHMENTS
// ============================================================================

/**
 * GET TEST CASE ATTACHMENTS
 *
 * Retrieves all attachments for a specific test case.
 *
 * @param {number|string} testCaseId - The test case ID
 * @returns {Promise<Array>} Array of attachment objects
 */
export const getTestCaseAttachments = async (testCaseId) => {
  try {
    const response = await attachmentsClient.get(`/attachments/testcases/${testCaseId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching test case attachments:', error)
    throw error
  }
}

/**
 * UPLOAD TEST CASE ATTACHMENT
 *
 * Uploads a file attachment to a test case.
 * Uses multipart/form-data with the field name "file".
 *
 * @param {number|string} testCaseId - The test case ID
 * @param {File} file - The file to upload
 * @param {function} onProgress - Optional progress callback (receives percent 0-100)
 * @returns {Promise<Object>} Created attachment object
 */
export const uploadTestCaseAttachment = async (testCaseId, file, onProgress) => {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await attachmentsClient.post(
      `/attachments/testcases/${testCaseId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              onProgress(percent)
            }
          : undefined
      }
    )
    return response.data
  } catch (error) {
    console.error('Error uploading test case attachment:', error)
    throw error
  }
}

// ============================================================================
// TEST STEP ATTACHMENTS
// ============================================================================

/**
 * GET TEST STEP ATTACHMENTS
 *
 * Retrieves all attachments for a specific test step.
 *
 * @param {number|string} testStepId - The test step ID
 * @returns {Promise<Array>} Array of attachment objects
 */
export const getTestStepAttachments = async (testStepId) => {
  try {
    const response = await attachmentsClient.get(`/attachments/teststeps/${testStepId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching test step attachments:', error)
    throw error
  }
}

/**
 * UPLOAD TEST STEP ATTACHMENT
 *
 * Uploads a file attachment to a test step.
 *
 * @param {number|string} testStepId - The test step ID
 * @param {File} file - The file to upload
 * @param {function} onProgress - Optional progress callback (receives percent 0-100)
 * @returns {Promise<Object>} Created attachment object
 */
export const uploadTestStepAttachment = async (testStepId, file, onProgress) => {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await attachmentsClient.post(
      `/attachments/teststeps/${testStepId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              onProgress(percent)
            }
          : undefined
      }
    )
    return response.data
  } catch (error) {
    console.error('Error uploading test step attachment:', error)
    throw error
  }
}

// ============================================================================
// EXECUTION ATTACHMENTS (for execution details modal)
// ============================================================================

/**
 * GET TEST CASE EXECUTION ATTACHMENTS
 *
 * Retrieves all attachments for a specific test case execution.
 * Uses runId + testCaseId to identify the execution.
 *
 * @param {number|string} runId - The test run ID
 * @param {number|string} testCaseId - The test case ID
 * @returns {Promise<Array>} Array of attachment objects
 */
export const getTestCaseExecutionAttachments = async (runId, testCaseId) => {
  try {
    const response = await attachmentsClient.get(`/attachments/runs/${runId}/testcases/${testCaseId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching test case execution attachments:', error)
    throw error
  }
}

/**
 * UPLOAD TEST CASE EXECUTION ATTACHMENT
 *
 * Uploads a file attachment to a test case execution.
 * Uses runId + testCaseId to identify the execution.
 *
 * @param {number|string} runId - The test run ID
 * @param {number|string} testCaseId - The test case ID
 * @param {File} file - The file to upload
 * @param {function} onProgress - Optional progress callback
 * @returns {Promise<Object>} Created attachment object
 */
export const uploadTestCaseExecutionAttachment = async (runId, testCaseId, file, onProgress) => {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await attachmentsClient.post(
      `/attachments/runs/${runId}/testcases/${testCaseId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              onProgress(percent)
            }
          : undefined
      }
    )
    return response.data
  } catch (error) {
    console.error('Error uploading test case execution attachment:', error)
    throw error
  }
}

/**
 * GET TEST STEP EXECUTION ATTACHMENTS
 *
 * Retrieves all attachments for a specific test step execution.
 * Uses runId + testStepId to identify the execution.
 *
 * @param {number|string} runId - The test run ID
 * @param {number|string} testStepId - The test step ID
 * @returns {Promise<Array>} Array of attachment objects
 */
export const getTestStepExecutionAttachments = async (runId, testStepId) => {
  try {
    const response = await attachmentsClient.get(`/attachments/runs/${runId}/teststeps/${testStepId}`)
    return response.data
  } catch (error) {
    console.error('Error fetching test step execution attachments:', error)
    throw error
  }
}

/**
 * UPLOAD TEST STEP EXECUTION ATTACHMENT
 *
 * Uploads a file attachment to a test step execution.
 * Uses runId + testStepId to identify the execution.
 *
 * @param {number|string} runId - The test run ID
 * @param {number|string} testStepId - The test step ID
 * @param {File} file - The file to upload
 * @param {function} onProgress - Optional progress callback
 * @returns {Promise<Object>} Created attachment object
 */
export const uploadTestStepExecutionAttachment = async (runId, testStepId, file, onProgress) => {
  try {
    const formData = new FormData()
    formData.append('file', file)

    const response = await attachmentsClient.post(
      `/attachments/runs/${runId}/teststeps/${testStepId}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: onProgress
          ? (progressEvent) => {
              const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total)
              onProgress(percent)
            }
          : undefined
      }
    )
    return response.data
  } catch (error) {
    console.error('Error uploading test step execution attachment:', error)
    throw error
  }
}

// ============================================================================
// DOWNLOAD AND DELETE
// ============================================================================

/**
 * DOWNLOAD ATTACHMENT
 *
 * Downloads an attachment file by its ID.
 * Returns the file as a Blob for client-side download.
 *
 * @param {number|string} attachmentId - The attachment ID
 * @param {string} fileName - Optional file name for download
 * @returns {Promise<Blob>} File blob
 */
export const downloadAttachment = async (attachmentId, fileName) => {
  try {
    const response = await attachmentsClient.get(
      `/attachments/${attachmentId}/download`,
      {
        responseType: 'blob'
      }
    )

    // Trigger browser download
    const blob = response.data
    const url = window.URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url

    // Try to get filename from Content-Disposition header or use provided name
    const contentDisposition = response.headers['content-disposition']
    let downloadFileName = fileName || 'download'

    if (contentDisposition) {
      const match = contentDisposition.match(/filename="?([^"]+)"?/)
      if (match && match[1]) {
        downloadFileName = match[1]
      }
    }

    link.setAttribute('download', downloadFileName)
    document.body.appendChild(link)
    link.click()

    // Cleanup
    link.parentNode.removeChild(link)
    window.URL.revokeObjectURL(url)

    return blob
  } catch (error) {
    console.error('Error downloading attachment:', error)
    throw error
  }
}

/**
 * DELETE ATTACHMENT
 *
 * Deletes an attachment by its ID.
 *
 * @param {number|string} attachmentId - The attachment ID
 * @returns {Promise<void>}
 */
export const deleteAttachment = async (attachmentId) => {
  try {
    await attachmentsClient.delete(`/attachments/${attachmentId}`)
  } catch (error) {
    console.error('Error deleting attachment:', error)
    throw error
  }
}

/**
 * GET ATTACHMENT PREVIEW BLOB URL
 *
 * Fetches an attachment as a blob and returns an object URL for preview.
 * This is needed because the API requires authentication which img src can't provide.
 *
 * @param {number|string} attachmentId - The attachment ID
 * @returns {Promise<string>} Object URL that can be used as img src
 */
export const getAttachmentPreviewBlobUrl = async (attachmentId) => {
  try {
    const response = await attachmentsClient.get(
      `/attachments/${attachmentId}/download`,
      {
        responseType: 'blob'
      }
    )
    const blob = response.data
    return window.URL.createObjectURL(blob)
  } catch (error) {
    console.error('Error fetching attachment preview:', error)
    throw error
  }
}
